import React, { useEffect, useState, useRef, useMemo, useId } from "react";
import "./SchedulerFilters.scss";
import { withTranslation } from "react-i18next";
import {
  useAllActiveOffices,
  useStaffDesignation,
} from "repositories/scheduler-repository";
import { useAllStaffByDesignationIdAndOfficeId } from "repositories/notes-repository";
import Select from "react-select";
import Loader from "components/Loader";
import styles from "../../../../pages/ManagerDashboard/Dashboard.module.scss";
import Text from "components/Text";
import FilterIcon from "../../../../../assets/images/clicked.svg";
import FilterDefaultIcon from "../../../../../assets/images/default.svg";
import ExpandableSearchBar from "accountOwner/pages/AddEvent/SchedulingAssistant/components/ExpandableSearchBar";
import searchStyles from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/EventsShiftsTab/EventsShiftsTab.module.scss";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import StaffDefaultImage from "../../../../../assets/images/staff-default-rounded.png";
import constants from "../../../../../constants";

function EmployeesFilter({
  t,
  selectedEmpFilter,
  setEmpFilter,
  ownerId,
  selectedOfficeFilter,
  selectedRoles = [],
  setSelectedRoles,
  ownerData,
  setUsersList,
  hideOwner = false, // Optional: hide owner from the list
  showProfilePic = false, // Optional: show profile picture instead of designation
  deduplicateByUserId = false, // Optional: deduplicate staff list by userId (keep first occurrence)
  hideRoleFilter = false, // Optional: hide the role filter dropdown and filter icon
  resetSearchTrigger = 0, // Optional: increment this to reset the search bar
  enabledOfficeFilter = false,
  idSelectionType = "id",
  showDesignationName = true,
  extraRoles = [],
  extraOffices = [],
}) {
  const { effectiveOwnerId } = useAccountOwnerCommon();
  // Unique per mounted instance so checkbox ids stay collision-free when several
  // EmployeesFilter instances are rendered on the same page.
  const instanceId = useId();
  const [rolesData, setRolesData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectDropdown, setSelectDropdown] = useState(true);
  const [staffData, setStaffData] = useState([]);
  const [filteredStaffData, setFilteredStaffData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedOption, setSelectedOption] = useState({
    value: "",
    label: "All Roles",
  });
  const [selectedOfficeOption, setSelectedOfficeOption] = useState({
    value: "",
    label: "All Offices",
  });
  const { isLoading: loadinStaffData, data: DashBoardRoleData } =
    useStaffDesignation(
      { effectiveOwnerId },
      { enabled: !!ownerId && !hideRoleFilter },
    );
  const { data, isLoading } = useAllStaffByDesignationIdAndOfficeId(
    selectedRoles,
    enabledOfficeFilter && selectedOfficeOption?.value
      ? [selectedOfficeOption.value]
      : selectedOfficeFilter,
    { ownerId, includeOwnerOffices: true },
    { enabled: !!ownerId },
  );
  const { data: officeData } = useAllActiveOffices(
    1,
    constants.OFFICE_PAGE_SIZE_100,
    {
      includeOwnerOffices: true,
      ownerId: ownerId,
    },
    "",
    { enabled: enabledOfficeFilter && !!ownerId },
  );

  const officeDataList = useMemo(() => {
    if (extraOffices?.length > 0) {
      return extraOffices;
    }
    if (officeData && officeData.data) {
      return [
        { value: "", label: "All Offices" },
        ...officeData.data.map((res) => ({ value: res.id, label: res.name })),
      ];
    }
    return [{ value: "", label: "All Offices" }];
  }, [officeData, extraOffices]);
  const extraRolesRef = useRef(extraRoles);
  extraRolesRef.current = extraRoles;
  const extraRolesKey =
    Array.isArray(extraRoles) && extraRoles.length > 1
      ? extraRoles.map((r) => String(r?.value ?? r?.id ?? "")).join(",")
      : "";

  useEffect(() => {
    if (extraRolesRef.current?.length > 1) {
      setRolesData((prev) => {
        if (
          prev?.length === extraRolesRef.current?.length &&
          prev.every(
            (r, i) =>
              String(r?.value ?? r?.id) ===
              String(
                extraRolesRef.current?.[i]?.value ??
                  extraRolesRef.current?.[i]?.id,
              ),
          )
        ) {
          return prev;
        }
        return extraRolesRef.current;
      });
      return;
    }
    if (!loadinStaffData && DashBoardRoleData?.length) {
      const flatData = DashBoardRoleData?.map(
        (item) => item?.designations,
      )?.flat();
      if (flatData?.length) {
        const data = flatData?.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));
        const newRoles = [{ value: "", label: "All Roles" }, ...data];
        setRolesData((prev) => {
          if (
            prev?.length === newRoles.length &&
            prev.every(
              (r, i) =>
                r?.value === newRoles[i]?.value &&
                r?.label === newRoles[i]?.label,
            )
          ) {
            return prev;
          }
          return newRoles;
        });
      }
    }
    // Depend on primitive key so unstable extraRoles array ref does not cause infinite loop
  }, [loadinStaffData, DashBoardRoleData, extraRolesKey]);

  const handleMemberType = (selected) => {
    setSelectedOption(selected);
    if (selected?.value) {
      setSelectedRoles([selected?.value]);
    } else {
      setSelectedRoles([]);
    }
  };

  useEffect(() => {
    if (!isLoading && data && data?.data) {
      let staffList = [...data.data];

      // Only add owner if hideOwner is false
      if (!hideOwner && ownerData) {
        let name = ownerData?.role?.id
          ? t("dashBoard.mySelf")
          : t("dashBoard.AccountOwner");
        const ownerStaff = {
          id: ownerData?.id,
          userId: ownerData?.userId || ownerData?.user?.id || ownerData?.id,
          user: { ...ownerData },
          designation: { name },
        };
        staffList = [ownerStaff, ...staffList];
      }

      // Deduplicate by userId if the prop is enabled (keep first occurrence)
      if (deduplicateByUserId) {
        const seenUserIds = new Set();
        staffList = staffList.filter((staff) => {
          const userId = staff?.userId || staff?.user?.userId;
          if (!userId || seenUserIds.has(userId)) {
            return false;
          }
          seenUserIds.add(userId);
          return true;
        });
      }

      setStaffData(staffList);
      setFilteredStaffData(staffList);
      if (setUsersList) {
        setUsersList(staffList);
      }
    }
  }, [isLoading, data, hideOwner, ownerData, t, deduplicateByUserId]);

  // Update selectAll state based on selectedEmpFilter and staffData
  // Only update if the value actually changes to prevent infinite loops
  useEffect(() => {
    const allSelected =
      staffData?.length > 0 && selectedEmpFilter?.length === staffData?.length;
    setSelectAll((prevSelectAll) => {
      // Only update if the value actually changed
      if (prevSelectAll !== allSelected) {
        return allSelected;
      }
      return prevSelectAll;
    });
  }, [selectedEmpFilter, staffData]);

  const handleChange = (e, item) => {
    if (e.target.checked) {
      setEmpFilter([...selectedEmpFilter, item[idSelectionType]]);
    } else {
      setEmpFilter((state) =>
        state.filter((thisId) => thisId !== item[idSelectionType]),
      );
    }
    setSelectAll(false);
  };

  const handleAllChange = (checked = false) => {
    setSelectAll(checked);
    if (checked) {
      setEmpFilter(
        staffData?.length
          ? staffData?.map((item) => item[idSelectionType])
          : [],
      );
    } else {
      setEmpFilter([]);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.toLowerCase();
    setSearchValue(value);

    if (value.trim() === "") {
      setFilteredStaffData(staffData);
    } else {
      setFilteredStaffData(
        staffData?.filter((item) =>
          `${item?.user?.firstName} ${item?.user?.lastName}`
            .toLowerCase()
            .includes(value),
        ),
      );
    }
  };

  // Update filteredStaffData when staffData changes (e.g., when role filter changes or resets)
  useEffect(() => {
    if (staffData && staffData.length > 0) {
      // If there's a search value, filter by it, otherwise show all staff
      if (searchValue.trim() === "") {
        setFilteredStaffData(staffData);
      } else {
        setFilteredStaffData(
          staffData.filter((item) =>
            `${item?.user?.firstName} ${item?.user?.lastName}`
              .toLowerCase()
              .includes(searchValue),
          ),
        );
      }
    } else {
      setFilteredStaffData([]);
    }
  }, [staffData, searchValue]);

  // Clear selectedEmpFilter only when role filter changes from a specific role to "All Roles"
  // Use refs to track previous values to detect actual changes
  const prevSelectedRoles = useRef(selectedRoles);

  useEffect(() => {
    // Only clear if:
    // 1. Role filter changed from having a value to being empty (switched to "All Roles")
    // 2. We have selections that need to be cleared
    const hadRoleBefore =
      prevSelectedRoles.current && prevSelectedRoles.current.length > 0;
    const hasRoleNow = selectedRoles && selectedRoles.length > 0;
    const roleWasCleared = hadRoleBefore && !hasRoleNow;

    if (roleWasCleared && selectedEmpFilter?.length > 0) {
      setEmpFilter([]);
    }

    // Update ref to track previous value
    prevSelectedRoles.current = selectedRoles;
  }, [selectedRoles, selectedEmpFilter, setEmpFilter]);

  // Reset role filter dropdown when selectedRoles becomes empty (from parent reset)
  useEffect(() => {
    if (!selectedRoles || selectedRoles.length === 0) {
      const allRolesOption = { value: "", label: "All Roles" };
      setSelectedOption(allRolesOption);
    }
  }, [selectedRoles]);

  // Reset search when selectedEmpFilter becomes empty (most reset functions do this)
  // Only reset if search has a value (to avoid resetting on initial load)
  const prevSelectedEmpFilter = useRef(selectedEmpFilter);
  useEffect(() => {
    const wasNotEmpty =
      prevSelectedEmpFilter.current && prevSelectedEmpFilter.current.length > 0;
    const isNowEmpty = !selectedEmpFilter || selectedEmpFilter.length === 0;

    if (wasNotEmpty && isNowEmpty && searchValue) {
      setSearchValue("");
      setFilteredStaffData(staffData);
    }

    prevSelectedEmpFilter.current = selectedEmpFilter;
  }, [selectedEmpFilter, staffData, searchValue]);

  // Reset search and role filter when resetSearchTrigger changes
  // Use ref to track previous trigger value to avoid resetting when staffData changes
  const prevResetTrigger = useRef(0);
  useEffect(() => {
    if (resetSearchTrigger > prevResetTrigger.current) {
      // Reset search
      setSearchValue("");
      if (staffData.length > 0) {
        setFilteredStaffData(staffData);
      }

      // Reset role filter dropdown display to "All Roles"
      // Don't call setSelectedRoles here - let parent handle that via resetFilter
      const allRolesOption = { value: "", label: "All Roles" };
      setSelectedOption(allRolesOption);

      prevResetTrigger.current = resetSearchTrigger;
    }
  }, [resetSearchTrigger, staffData]);

  return (
    <>
      {isLoading && <Loader />}
      <div className={styles.flex_col_div}>
        <div className={styles.filter_search_icon_wrapper}>
          <div
            className={
              searchStyles["search-form"] + " w-100 " + styles["search-box"]
            }
          >
            <ExpandableSearchBar
              placeholder={t("accountOwner.searchByName")}
              searchValue={searchValue}
              setSearchValue={handleSearchChange}
              expanded
            />
          </div>
          {!hideRoleFilter && (
            <div
              onClick={() => {
                setSelectDropdown(!selectDropdown);
              }}
            >
              {selectDropdown ? (
                <img
                  src={FilterIcon}
                  alt="FilterIcon"
                  className={styles.filter_icon}
                />
              ) : (
                <img
                  src={FilterDefaultIcon}
                  alt="FilterIcon"
                  className={styles.filter_icon_default}
                />
              )}
            </div>
          )}
        </div>
        {!hideRoleFilter && selectDropdown && (
          <div
            className={
              styles.select_container_new +
              " " +
              "member-filter review-rating-filter all-option custom-scroll " +
              (enabledOfficeFilter
                ? "employees-filter-dual-select-panel d-flex"
                : "employees-filter-single-select-panel")
            }
          >
            <Select
              className={styles.react_select_box + " react-select-container"}
              classNamePrefix="react-select"
              options={rolesData}
              value={selectedOption}
              onChange={handleMemberType}
              isSearchable={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({
                  ...base,
                  width: "max-content",
                  minWidth: "100%",
                }),
              }}
            />

            {enabledOfficeFilter && (
              <Select
                className={styles.react_select_box + " react-select-container"}
                classNamePrefix="react-select"
                options={officeDataList}
                value={selectedOfficeOption}
                onChange={setSelectedOfficeOption}
                isSearchable={false}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({
                    ...base,
                    width: "max-content",
                    minWidth: "100%",
                  }),
                }}
              />
            )}
          </div>
        )}
        <ul className={["filter-list"]}>
          {filteredStaffData?.length ? (
            <ul>
              <li>
                <div className="ch-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      onChange={(e) => handleAllChange(e.target.checked)}
                      checked={selectAll}
                    />
                    <span>{t("taskManagement.allStaff")}</span>
                  </label>
                </div>
              </li>
              <hr className="mr-4" />
            </ul>
          ) : (
            <span>{t("dashBoard.notFoundStaff")}</span>
          )}

          {filteredStaffData &&
            filteredStaffData?.map((item, key) => {
              const profilePic = item?.user?.profilePic || item?.profilePic;
              const checkboxId = `${instanceId}-staff-${item?.[idSelectionType] ?? key}`;

              return (
                <div className={styles["checkbox_wrapper_filter"]} key={key}>
                  <div
                    className={
                      styles["check_box_width"] +
                      " " +
                      "ch-checkbox c-field all-event-checkbox m-0"
                    }
                  >
                    <label>
                      <input
                        type="checkbox"
                        id={checkboxId}
                        onChange={(e) => handleChange(e, item)}
                        checked={selectedEmpFilter?.includes(
                          item[idSelectionType],
                        )}
                      />
                      <span className={styles["checkbox_height"]}></span>
                    </label>
                  </div>
                  <div
                    className={
                      styles["name_office"] +
                      " " +
                      styles["clickable_name_office"]
                    }
                  >
                    <label
                      className="m-0 d-flex align-items-center"
                      htmlFor={checkboxId}
                    >
                      <div>
                        <Text
                          size="14px"
                          weight="600"
                          color="#102C42"
                          className={styles.office_name}
                        >
                          {`${item?.user?.firstName} ${item?.user?.lastName}`}
                        </Text>
                        {!showProfilePic && showDesignationName && (
                          <Text size="12px" weight="400" color="#87928D">
                            {item?.designation?.name}
                          </Text>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              );
            })}
        </ul>
      </div>
    </>
  );
}

export default withTranslation()(EmployeesFilter);
